/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

import { PublicLinkPageQuery } from '../graphql/PublicLinkPageQuery.graphql';

import type {
	usePublicLinkEnablePageMutationPublicLinkEnablePageMutation as usePublicLinkEnablePageMutationPublicLinkEnablePageMutation$data,
	usePublicLinkEnablePageMutationPublicLinkEnablePageMutationVariables as usePublicLinkEnablePageMutationPublicLinkEnablePageMutation$variables,
} from './__types__/usePublicLinkEnablePageMutationPublicLinkEnablePageMutation';

const i18n = defineMessages({
	success: {
		id: 'public-links.usePublicLinkEnablePageMutation.success.title',
		defaultMessage: 'Public link is now on for this content.',
		description: 'Message shown when the public link is successfully turned on.',
	},
	errorTitle: {
		id: 'public-links.usePublicLinkEnablePageMutation.error.title',
		defaultMessage: 'We couldn’t turn on the public link.',
		description: 'Message shown when there is an error while trying to turn on the public link.',
	},
	errorBody: {
		id: 'public-links.usePublicLinkEnablePageMutation.error.body',
		defaultMessage:
			'Open the Share and Restrict dialog to check the current status, or to turn on the public link.',
		description: 'Message shown when there is an error while trying to turn on the public link.',
	},
});

export const usePublicLinkEnablePageMutation = ({
	pageId,
	onCompleted,
	onError,
	flags,
}: {
	pageId: string;
	onCompleted: () => void;
	onError: (error) => void;
	flags: FlagsStateContainer;
}) => {
	const { formatMessage } = useIntl();
	const [enablePublicLinkMutation] = useMutation<
		usePublicLinkEnablePageMutationPublicLinkEnablePageMutation$data,
		usePublicLinkEnablePageMutationPublicLinkEnablePageMutation$variables
	>(
		graphql`
			mutation usePublicLinkEnablePageMutationPublicLinkEnablePageMutation($pageId: ID!) {
				enablePublicLinkForPage(pageId: $pageId) {
					pageId
				}
			}
		`,
		{
			refetchQueries: [
				{
					query: PublicLinkPageQuery,
					variables: { pageId },
				},
			],
			onCompleted: () => {
				void flags.showFlag({
					id: 'revokeFromRestrictionsFlagId',
					title: formatMessage(i18n.success),
					type: 'success-circle',
					close: 'auto',
				});
				onCompleted();
			},
			onError: (error) => {
				void flags.showFlag({
					id: 'revokeFromRestrictionsErrorFlagId',
					title: formatMessage(i18n.errorTitle),
					description: formatMessage(i18n.errorBody),
					type: 'error',
					close: 'auto',
				});
				onError(error);
			},
		},
	);

	const enablePublicLinkForPage =
		(pageId: usePublicLinkEnablePageMutationPublicLinkEnablePageMutation$variables['pageId']) =>
		() =>
			enablePublicLinkMutation({
				variables: { pageId },
			});

	return {
		enablePublicLinkForPage: enablePublicLinkForPage(pageId),
	};
};
